import { AlertTriangle, CheckCircle2, MinusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatDateByDayjs } from '@/utils';
import { OrderSla, OrderSlaNotTrackedReason } from '@/types/sla';

interface SlaCellProps {
  sla?: OrderSla | null;
  /**
   * Show the `not-tracked` state instead of a blank cell.
   *
   * The integration guide says to leave the SLA cell blank when an order is
   * not tracked, and it is right for the LIST: `new`, `scheduled`, `resend`
   * and every terminal status are untracked, so a grey chip on most rows is
   * noise that makes the breached rows harder to find. On the detail page
   * there is one order and room to say why, so it is worth saying.
   */
  showNotTracked?: boolean;
}

const REASON_KEY: Record<OrderSlaNotTrackedReason, string> = {
  'status-not-monitored': 'sla_reason_status_not_monitored',
  'merchant-excluded': 'sla_reason_merchant_excluded',
  'no-region': 'sla_reason_no_region',
};

/**
 * The SLA state of one order: icon, due time, and a state chip — all visible,
 * nothing behind a hover. On a list a merchant is scanning for the late ones,
 * and a state you have to hover to read is a state nobody reads.
 *
 * Colours follow the integration guide's own states table: green on-track,
 * red breached, grey not-tracked.
 *
 * 🔴 SLA is decoration. Core sends `sla: null` whenever the automations
 * service is off, unreachable, rate-limited or simply had nothing to say, and
 * every one of those renders a quiet dash — never a crash, never a broken cell.
 *
 * `state` comes from the service and is computed against its own UTC clock, so
 * this does not re-derive on-track/breached from the browser's clock — a device
 * with a skewed clock would otherwise disagree with the engine and with ops.
 */
const SlaCell = ({ sla, showNotTracked = false }: SlaCellProps) => {
  const { t } = useTranslation();

  if (!sla) {
    return <span className='text-sd-muted-foreground'>—</span>;
  }

  const breached = sla.state === 'breached';
  // 🔴 M-2 — a CLOSED world. The engine is owned by another team and can add a
  // state ('at-risk', 'paused', …) without telling us. Treating "not breached"
  // as "on track" gave every unknown state the single most reassuring label
  // there is. An unrecognised state shows the due time and NO chip: it claims
  // nothing rather than claiming the most comforting thing available.
  const onTrack = sla.state === 'on-track';

  // 🔴 STATE FIRST, and a breach outranks a missing due time. Keying
  // not-tracked off `!dueAt` alone sent a `breached` order with a null or
  // unparseable `dueAt` down the not-tracked road — which the LIST hides
  // entirely, so a real breach vanished from the one screen breaches are for.
  // Core only guarantees `orderUid` and `state`; every other field is
  // passthrough from a service another team deploys, so `dueAt` going missing
  // is a live possibility, not a hypothetical. Found by BOXY::ALL building the
  // app half against this same contract.
  const notTracked = !breached && (sla.state === 'not-tracked' || !sla.dueAt);

  if (notTracked) {
    if (!showNotTracked) {
      return <span className='text-sd-muted-foreground'>—</span>;
    }

    const reasonKey = sla.reason ? REASON_KEY[sla.reason] : undefined;

    return (
      <span className='inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]'>
        <Chip tone='muted' icon={MinusCircle} label={String(t('sla_not_tracked'))} />
        {reasonKey ? (
          <span className='text-sd-muted-foreground'>{String(t(reasonKey))}</span>
        ) : null}
      </span>
    );
  }

  // A breach with no due time still says "breached" — it just has no time.
  const due = sla.dueAt ? formatDateByDayjs(sla.dueAt, 'D MMM, HH:mm') : null;

  return (
    <span className='inline-flex flex-wrap items-center gap-x-2 gap-y-1 whitespace-nowrap text-[13px]'>
      {due ? (
        <span className={breached ? 'font-medium text-red-600' : 'text-sd-foreground'}>{due}</span>
      ) : null}
      {breached ? (
        <Chip
          tone='danger'
          icon={AlertTriangle}
          label={
            sla.breach
              ? `${String(t('sla_breached'))} · ${String(t('escalation'))} ${sla.breach.escalationLevel}`
              : String(t('sla_breached'))
          }
        />
      ) : null}
      {onTrack ? (
        <Chip tone='success' icon={CheckCircle2} label={String(t('sla_on_track'))} />
      ) : null}
    </span>
  );
};

const TONES = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  danger: 'border-red-200 bg-red-50 text-red-700',
  muted: 'border-sd-border bg-sd-muted text-sd-muted-foreground',
} as const;

const Chip = ({
  tone,
  icon: Icon,
  label,
}: {
  tone: keyof typeof TONES;
  icon: typeof AlertTriangle;
  label: string;
}) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border border-solid px-2 py-0.5 text-[11px] font-semibold leading-none ${TONES[tone]}`}
  >
    <Icon className='h-3 w-3 shrink-0' strokeWidth={2.25} aria-hidden />
    {label}
  </span>
);

export default SlaCell;
